import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import Modal from "common/Modal.jsx";
import LoadingIcon from "common/Loading";
import { checkActivationAllowed, parseSystemSoftwareDetail, sendForceStandalone, showRemoveClusterConfigurationWarning } from "./utils";
import CheckBox from "common/Checkbox";
import Table from "common/graphs/Table/Table";
import { TextWithIcon } from "common/graphs/Table/TableRenders";
import InstallModal from "./Install";

const doesNothing = () => {};

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const ReloadAllDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  label{
    margin-bottom: 0px !important;
    line-height: 16px;
  }

  i{
    font-size: 20px;
    margin-top: -2px;
  }

  margin-bottom: 30px;
`;

const tellTargetNode = bqn =>
  bqn !== null && bqn !== undefined ? ` for BQN ${bqn}` : "";

const BootModal = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const actions = useContext(ActionsContext);
  const [errorFooter, setErrorFooter] = useState("");
  const { name, version, bqn } = params;
  const [loading, setLoading] = useState(false);

  const apply = () => { 
    setLoading(true);
    return ifCl
      .run(
        `system software boot on ${name} ${version}${
          bqn !== undefined && bqn !== null ? ` bqn-id ${bqn}` : ""
        }`
      )
      .then((response) => {
        setLoading(false);
        if (response) {
          console.log("software boot .<%s>.error: <%s>", response);
          throw `Warning booting: ${response}`;
        } else {
          actions.send("do-load");
          setOpen(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  useEffect(
    () =>
      actions.recv(`boot-software`, ({ name, version, bqn }) => {
        setParams({ name, version, bqn });
        setErrorFooter("");
        setOpen(true);
        setLoading(false);
      }),
    []
  );

  if (!open) {
    return null;
  }
  return (
    <Modal
      title={`Set boot flag to ${name} - ${version}${tellTargetNode(bqn)}?`}
      superIcon="warning"
      onClose={() => setOpen(false)}
      className="modal-software-install"
      closeLabel="CANCEL"
      applyLabel="OK"
      onApply={() => apply()}
      content={() => (
        <>
          <div>This package will be the one loaded after reboot.</div>
          {loading ? (
            <LoadingWrapper>
              <LoadingIcon />
            </LoadingWrapper>
          ) : null}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

const ReloadModal = ({ softwareDetail, softwareBqnId }) => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const actions = useContext(ActionsContext);
  const [errorFooter, setErrorFooter] = useState("");
  const { name, version, bqn } = params;
  const [loading, setLoading] = useState(false);
  const [showReloadAll, setShowReloadAll] = useState(false);
  const [reloadAll, setReloadAll] = useState(false);
  const [forceStandalone, setForceStandalone] = useState(false);

  const {
    onlineBqns,
    clusterStatus: { mode },
    loginBqnId,
    bqnId,
  } = useContext(BqnContext);

  useEffect(() => {
    return actions.recv(`reload-software`, ({ name, version, bqn }) => {
      setParams({ name, version, bqn });
      setErrorFooter("");
      setShowReloadAll(false);
      setReloadAll(false);
      setLoading(false);

      if(softwareDetail){
        const result = checkActivationAllowed(name, version, softwareDetail, bqn, loginBqnId, mode)
        if(result.reloadAllowed){
          setForceStandalone(result.forceStandalone);
          setOpen(true);
        }else{
          showRemoveClusterConfigurationWarning();
        }
      }
    });
  }, [softwareDetail]);

  useEffect(() => {
    if (open) {
      checkShouldShowReloadAll();
    }
  }, [open]);

  function checkShouldShowReloadAll() {
    setShowReloadAll(
      mode === "standalone"
        ? false
        : softwareBqnId[`${name}-${version}`].length === onlineBqns.length,
    );
  }

  const apply = () => {
    const next  = () => (reloadAll ? doActivateAll() : doActivateOne());
    if(forceStandalone){
      sendForceStandalone(bqnId, next)
    }else{
      next();
    }
  }

  const doActivateOne = () => {
    setErrorFooter("");
    setLoading(true);
    const isSelfReload = parseFloat(bqn) === parseFloat(loginBqnId);
    const cmd = `system software reload ${name} ${version} force${
      mode === "cluster" && !isSelfReload ? ` bqn-id ${bqn}` : ""
    }`;
    return ifCl
      .run(cmd)
      .then((response) => {
        if (response) {
          console.log("software reload .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        } else if(isSelfReload){
          setTimeout(() => login.doLogout(), 5000)
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  const doActivateAll = () => {
    setErrorFooter("");
    setLoading(true);

    const cmdsOthers = onlineBqns
      .filter(({ bqnId }) => parseFloat(bqnId) !== parseFloat(loginBqnId))
      .map(({ bqnId }) => {
        return `system software reload ${name} ${version} force bqn-id ${bqnId}`;
      });

    const cmdSelf = `system software reload ${name} ${version} force`;

    return new Promise((resolve, reject) => {
      Promise.all(cmdsOthers.map((cmd) => ifCl.run(cmd)))
        .then((responses) => {
          const error = responses.find((response) => response);
          if (error) {
            return reject(error);
          }

          ifCl
            .run(cmdSelf)
            .then((response) => {
              if (response) {
                reject(response);
              } else {
                setTimeout(() => login.doLogout(), 5000)
                resolve();
              }
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    })
      .then((response) => {
        if (response) {
          throw `Warning: ${response}`;
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  if (!open) {
    return null;
  }
  return (
    <Modal
      title={`Reload package ${name}-${version}${
        bqn !== undefined ? ` for BQN ${bqn}` : ""
      }?`}
      superIcon="cached"
      className="modal-software-install"
      onClose={() => setOpen(false)}
      closeLabel="CANCEL"
      applyLabel={loading ? null : "OK"}
      onApply={loading ? doesNothing : () => apply()}
      content={() => (
        <>
          {showReloadAll && !loading && (
            <ReloadAllDiv>
              <CheckBox
                checked={reloadAll}
                onChange={(value) => setReloadAll(value)}
              />
              <label>Reload this package in all BQNs in the cluster</label>
            </ReloadAllDiv>
          )}
          {!loading ? (
            <div>
              <b>WARNING</b>: This will result in a traffic interruption
              of 10 seconds to 2 minutes, depending on the number of
              interfaces, CPU cores, and memory size,
              and it will be necessary to reload the page and login again.
            </div>
          ) : null}
          {loading ? (
            <div>
              <b>Activating</b>: Wait a few seconds and refresh the browser.
            </div>
          ) : null}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const FooterMessage = styled.div`
color: red;
 ul {
    padding: 0 0 0 15px;
    margin: 0px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
      text-align: left;
    }
  }
`

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [errorFooter, setErrorFooter] = useState("");

  const handleApply = () => {
    const cmds = itemsToDelete.map(
      ({ name, version, bqn }) =>
        `system software remove ${name} ${version}${
          mode === "cluster" ? ` bqn-id ${bqn}` : ""
        }`
    );
    return ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then((response) => {
        if (response) {
          console.log("software delete .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        } else {
          setShowDeleteModal(false);
          whenDone();
        }
      })
      .catch((error) => {
        setErrorFooter(error);
      });
  };

  if (!showDeleteModal) {
    return null;
  }

  const { delete: itemsToDelete, notDelete } = items.reduce((acc, item) => {
    if (item.active || item.boot) {
      acc.notDelete.push(item);
    } else {
      acc.delete.push(item);
    }
    return acc;
  }, {delete: [], notDelete: []});

  const title =
    itemsToDelete.length === 1
      ? `Delete package ${itemsToDelete[0].name} - ${itemsToDelete[0].version}${
          mode === "cluster" ? ` for BQN ${itemsToDelete[0].bqn}` : ""
        }?`
      : `Delete ${itemsToDelete.length} packages?`;

  const includeActiveOrBootPkg = items.some((item) => item.active) || items.some((item) => item.boot);

  return (
    <Modal
      title={title}
      superIcon="delete"
      className="modal-software-install"
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      applyLabel="OK"
      onApply={handleApply}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {itemsToDelete.length > 1 && itemsToDelete.length < 100 ? (
            <ul>
              {itemsToDelete.map((item, i) => (
                <li key={`${item.bqn}-${item.version}-${i}`}>
                  {item.name}-{item.version}
                  {mode === "cluster" ? ` (BQN ${item.bqn})` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </DeleteModalWrapper>
      )}
      footerMessage={
        errorFooter !== "" || includeActiveOrBootPkg ? (
          <>
            <span className="modal-err-msg color-red">
              {errorFooter !== "" ? <h5>{errorFooter}</h5> : null}
            </span>
            {includeActiveOrBootPkg && errorFooter === "" ? (
              <FooterMessage>
                <p>These packages cannot be deleted.</p>
                <ul>
                  {notDelete.map((item, i) => (
                    <li key={i}>
                      {item.name}-{item.version}
                    </li>
                  ))}
                </ul>
              </FooterMessage>
            ) : null}
          </>
        ) : null
      }
    />
  );
};

const ExtraMenuWrapper = styled.div`
  margin: 0 0 10px;
`;

const getColumns = (mode, actions, setDeleteSelected, setShowDeleteModal) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
    {
      label: "NAME",
      id: "NAME",
      numeric: false,
    },
    {
      label: "VERSION",
      id: "VERSION",
      numeric: false,
    },
    {
      label: "ACTIVE",
      id: "ACTIVE",
      numeric: false,
      renderCell: (value) => {
        if (!value) {
          return null;
        }
        return (
          <TextWithIcon
            icon={value === "yes" ? "check_box" : "check_box_outline_blank"}
            value=""
          />
        );
      },
    },
    {
      label: "BOOT",
      id: "BOOT",
      numeric: false,
      renderCell: (value, row) => {
        if (!value) {
          return null;
        }
        const className = value === "no" ? "clickable" : "";
        return (
          <TextWithIcon
            icon={value === "yes" ? "check_box" : "check_box_outline_blank"}
            value=""
            className={className}
            onClick={() => {
                actions.send("boot-software", {
                  name: row["NAME"],
                  version: row["VERSION"],
                  bqn: mode === 'cluster' ? row["BQN"] : null
                });
            }}
          />
        );
      },
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Reload",
          icon: "cached",
          onClick: (row) => {
            const bqn = row["BQN"];
            const name = row["NAME"];
            const version = row["VERSION"];
            actions.send("reload-software", { name, version, bqn });
          },
          hide: (row) => row["RELOAD"] === "no",
        },
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            const bqn = row["BQN"];
            const name = row["NAME"];
            const version = row["VERSION"];
            setDeleteSelected([{ name, version, bqn }]);
            setShowDeleteModal(true);
          },
          hide: (row) => row["ACTIVE"] === "yes" || row["BOOT"] === "yes",
        },
      ],
    },
  ];
};

const List = ({ response, softwareDetail, softwareBqnId}) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row, rowIndex) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, { id: rowIndex });
    return newEntry;
  });
  const [filter, setFilter] = useState(false);
  const filteredRows = filter ? newRows.filter((row) => row["ACTIVE"] === "yes" || row["BOOT"] === "yes") : newRows;
  return (
    <>
      <ExtraMenuWrapper>
        <button
          type="button"
          className="btn btn-default waves-effect shadow-none hidden-to-operators"
          onClick={() => actions.send("install-software")}
        >
          <i className="material-icons">add_circle</i>
          <span>Install new package...</span>
        </button>
        <button
          type="button"
          className="btn btn-default waves-effect shadow-none hidden-to-operators"
          onClick={() => setFilter(!filter)}
        >
          <i className="material-icons">
            {filter ? "expand_more" : "expand_less"}
          </i>
          <span>
            {filter ? "Show all packages" : "Show only active+boot packages"}
          </span>
        </button>
      </ExtraMenuWrapper>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={filteredRows}
          sort={true}
          columns={getColumns(mode, actions, setDeleteSelected, setShowDeleteModal)}
          search={true}
          csvPrefix="software-packages"
          rowsPerPageNr={50}
          selected={selected}
          setSelected={setSelected}
          deleteSelected={(values) => {
            const selectedItems = filteredRows
              .filter((row) => values.includes(row.id))
              .map((row) => ({
                name: row["NAME"],
                version: row["VERSION"],
                bqn: row["BQN"],
                active: row["ACTIVE"] === "yes",
                boot: row["BOOT"] === "yes"
              }));
            setDeleteSelected(selectedItems);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          actions.send("do-load");
        }}
      />
      <ReloadModal
        softwareDetail={softwareDetail}
        softwareBqnId={softwareBqnId}
      />
      <BootModal />
    </>
  );
};

const SoftwareList = () => {
  const [request, setRequest] = useState(null);
  const [softwareDetail, setSoftwareDetail] = useState();
  const [softwareBqnId, setSoftwareBqnId] = useState();

  const actions = useContext(ActionsContext);

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const doRequest = () => {
    const cmd =
      mode === "cluster" && bqnId !== "all"
        ? `show system software bqn-id ${bqnId}`
        : "show system software detail";
    return ifCl.run(cmd).then((response) => ({ response }));
  };

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => {
    doLoad();
    return actions.recv("do-load", doLoad);
  }, [bqnId]);

  useEffect(() => {
    if (request) {
      request.then(({ response }) => {
        const { softwareList, softwareBqnId } =
          parseSystemSoftwareDetail(response);
        setSoftwareDetail(softwareList);
        setSoftwareBqnId(softwareBqnId);
      });
    }
  }, [request]);

  return (
    <>
      <InstallModal softwareDetail={softwareDetail} />
      {request === null ? null : (
        <Request during={request}>
          {(result) => (
            <List
              {...result}
              softwareDetail={softwareDetail}
              softwareBqnId={softwareBqnId}
            />
          )}
        </Request>
      )}
    </>
  );
};

export default SoftwareList;
